/**
 * PublicPhotosBlock — in-flow photo review at the public export gate.
 *
 * Replaces the static PUBLIC_PHOTO_ADVISORY text block in PreMintWarningPanel
 * with a live thumbnail rail showing the REDACTED derivative of every
 * public-confirmed photo. Tapping a tile opens a review-only modal so the
 * owner can make a photo private without leaving the export flow.
 *
 * States:
 *   - Zero public photos: single-line notice; no rail.
 *   - Loading: skeleton tile row.
 *   - With photos: header count + scroll-snap thumbnail rail + advisory.
 *
 * Modal shares overlay/dialog/preview-frame shapes with PhotoPublishModal.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No navigator.onLine.
 *   - No localStorage/sessionStorage.
 *   - No Timestamp/FieldValue.
 */

import { Button } from '@/components/Button';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useModalFocus } from '@/hooks/useModalFocus';
import { useConfirmPublish } from '@/hooks/usePhotoActions';
import { usePhotos } from '@/hooks/usePhotos';
import { usePreviewImage } from '@/hooks/usePreviewImage';
import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Shared overlay / dialog shapes — matches PhotoPublishModal so the two modal
// surfaces feel identical. Replicated here rather than exported from the
// source so PublicPhotosBlock has no cross-feature import.
// ---------------------------------------------------------------------------

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.88)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: SPACE.md,
};

const dialogStyle: CSSProperties = {
  background: T.bg,
  borderRadius: RADIUS.lg,
  width: '100%',
  maxWidth: 560,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const modalHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${SPACE.md}px ${SPACE.md}px ${SPACE.sm}px`,
  borderBottom: `1px solid ${T.border}`,
  flexShrink: 0,
};

const closeBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: T.text,
  borderRadius: RADIUS.md,
  padding: SPACE.xs,
  minWidth: 44,
  minHeight: 44,
  transition: `background ${TRANSITION.fast}`,
};

const previewFrameStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '4 / 3',
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
};

const modalFooterStyle: CSSProperties = {
  display: 'flex',
  gap: SPACE.sm,
  padding: `${SPACE.sm}px ${SPACE.md}px ${SPACE.md}px`,
  borderTop: `1px solid ${T.border}`,
  flexShrink: 0,
  justifyContent: 'flex-end',
};

// ---------------------------------------------------------------------------
// ThumbnailTile — 72×72px button showing the REDACTED derivative thumbnail.
// ---------------------------------------------------------------------------

interface ThumbnailTileProps {
  photoId: string;
  index: number;
  total: number;
  onClick: () => void;
  tileRef: (el: HTMLButtonElement | null) => void;
}

function ThumbnailTile({ photoId, index, total, onClick, tileRef }: ThumbnailTileProps) {
  const imgState = usePreviewImage(photoId, true);

  const tileStyle: CSSProperties = {
    width: 72,
    height: 72,
    flexShrink: 0,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    overflow: 'hidden',
    position: 'relative',
    background: T.card,
    cursor: 'pointer',
    padding: 0,
    display: 'block',
  };

  return (
    <button
      ref={tileRef}
      type="button"
      aria-label={`Review public photo ${index + 1} of ${total}`}
      onClick={onClick}
      style={tileStyle}
    >
      {imgState.status === 'loading' || imgState.status === 'idle' ? (
        // Shimmer skeleton
        <div aria-hidden="true" style={{ width: '100%', height: '100%', background: T.border }} />
      ) : imgState.status === 'ready' ? (
        <img
          src={imgState.url}
          alt=""
          aria-hidden="true"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        // not_found or error
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: T.textMuted,
              fontFamily: F.body,
              textAlign: 'center',
              padding: SPACE.xs,
            }}
          >
            Preview unavailable
          </span>
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ReviewModal — review-only: shows one redacted photo with Close + Make private.
// Focus is managed by useModalFocus; Escape closes.
// ---------------------------------------------------------------------------

interface ReviewModalProps {
  vehicleId: string;
  photoId: string;
  index: number;
  total: number;
  onClose: () => void;
  onMadePrivate: (photoId: string) => void;
}

function ReviewModal({
  vehicleId,
  photoId,
  index,
  total,
  onClose,
  onMadePrivate,
}: ReviewModalProps) {
  const { dialogRef, initialRef: closeRef } = useModalFocus<HTMLDivElement, HTMLButtonElement>(
    true,
  );
  const confirmMutation = useConfirmPublish(vehicleId);
  const imgState = usePreviewImage(photoId, true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleMakePrivate() {
    confirmMutation.mutate(
      { photoId, confirmed: false },
      {
        onSuccess: () => {
          onMadePrivate(photoId);
        },
      },
    );
  }

  const bodyStyle: CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: SPACE.md,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.md,
    minHeight: 0,
  };

  return (
    <div style={overlayStyle} role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Public photo ${index + 1} of ${total}`}
        style={dialogStyle}
      >
        <div style={modalHeaderStyle}>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: F.body,
              color: T.text,
            }}
          >
            Public photo {index + 1} of {total}
          </p>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close photo review"
            style={closeBtnStyle}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={previewFrameStyle}>
            {imgState.status === 'loading' || imgState.status === 'idle' ? (
              <p
                role="status"
                aria-live="polite"
                style={{ fontSize: 13, fontFamily: F.body, color: T.textMuted, margin: 0 }}
              >
                Loading preview…
              </p>
            ) : imgState.status === 'ready' ? (
              <img
                src={imgState.url}
                alt="Redacted preview. Faces are blurred automatically, but license plates and other details are not. This is exactly what anyone with the link will see."
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <p
                role="alert"
                style={{
                  fontSize: 13,
                  fontFamily: F.body,
                  color: T.red,
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                Preview unavailable.
              </p>
            )}
          </div>

          {confirmMutation.isError ? (
            <p role="alert" style={{ margin: 0, fontSize: 13, fontFamily: F.body, color: T.red }}>
              Something went wrong. Try again.
            </p>
          ) : null}
        </div>

        <div style={modalFooterStyle}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={confirmMutation.isPending}
          >
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleMakePrivate}
            loading={confirmMutation.isPending}
          >
            Make private
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PublicPhotosBlock — main export
// ---------------------------------------------------------------------------

export interface PublicPhotosBlockProps {
  vehicleId: string;
}

export function PublicPhotosBlock({ vehicleId }: PublicPhotosBlockProps) {
  const { data, isLoading } = usePhotos(vehicleId);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const tileRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const publicPhotos = useMemo(
    () => (data ?? []).filter((p) => p.publicConfirmed === true),
    [data],
  );

  const count = publicPhotos.length;
  const selectedPhoto = selectedIndex !== null ? (publicPhotos[selectedIndex] ?? null) : null;

  // Card chassis — matches PreMintWarningPanel's warningBlock:
  //   1px T.border · RADIUS.md · T.card bg
  const blockStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
    padding: SPACE.sm,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    background: T.card,
  };

  function openTile(index: number) {
    setSelectedIndex(index);
  }

  function closeModal() {
    const prevIndex = selectedIndex;
    setSelectedIndex(null);
    // useModalFocus restores focus to the element active at modal-open time
    // (the tile button). No manual restore needed for the Close path.
    // For safety, we also store the index in case focus needs nudging.
    if (prevIndex !== null) {
      setTimeout(() => {
        tileRefs.current[prevIndex]?.focus();
      }, 0);
    }
  }

  function handleMadePrivate(_photoId: string) {
    const prevIndex = selectedIndex ?? 0;
    setSelectedIndex(null);
    setAnnouncement('Photo made private and removed from the public page.');
    // useConfirmPublish already invalidates photoKeys on success, which
    // triggers a re-fetch. publicPhotos will update on next render.
    // Attempt to focus the successor tile (or predecessor if last item).
    setTimeout(() => {
      const targetIndex = Math.max(0, prevIndex - 1);
      tileRefs.current[targetIndex]?.focus();
    }, 0);
  }

  // ---------------------------------------------------------------------------
  // Zero state — no public photos
  // ---------------------------------------------------------------------------

  if (!isLoading && count === 0) {
    return (
      <div data-testid="pre-mint-photo-advisory" style={blockStyle}>
        <p style={{ margin: 0, fontSize: 12, color: T.text, lineHeight: 1.6, fontFamily: F.body }}>
          No photos will appear on this page. Photos only go public after you review each one.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Loading state — skeleton tiles
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div data-testid="pre-mint-photo-advisory" style={blockStyle}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.xs }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: F.body }}>
            Photos on this page
          </span>
        </div>
        <div
          role="group"
          aria-label="Public photos"
          style={{
            display: 'flex',
            gap: SPACE.xs,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            paddingBottom: SPACE.xs,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              style={{
                width: 72,
                height: 72,
                flexShrink: 0,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.md,
                background: T.border,
                scrollSnapAlign: 'start',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // With public photos — header + thumbnail rail + advisory
  // ---------------------------------------------------------------------------

  return (
    <div data-testid="pre-mint-photo-advisory" style={blockStyle}>
      {/* Polite live region for "made private" announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: -9999,
          width: 1,
          height: 1,
          overflow: 'hidden',
        }}
      >
        {announcement}
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.xs }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: F.body }}>
          Photos on this page
        </span>
        <span style={{ fontSize: 12, color: T.textMuted, fontFamily: F.body }}>
          · {count} public
        </span>
      </div>

      {/* Thumbnail rail — horizontal scroll-snap */}
      <div
        role="group"
        aria-label="Public photos"
        style={{
          display: 'flex',
          gap: SPACE.xs,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingBottom: SPACE.xs,
        }}
      >
        {publicPhotos.map((photo, i) => (
          <div key={photo.photoId} style={{ scrollSnapAlign: 'start', flexShrink: 0 }}>
            <ThumbnailTile
              photoId={photo.photoId}
              index={i}
              total={count}
              onClick={() => openTile(i)}
              tileRef={(el) => {
                tileRefs.current[i] = el;
              }}
            />
          </div>
        ))}
      </div>

      {/* Advisory — shown only when count > 0; truthful: faces blurred, plates not */}
      <p
        style={{ margin: 0, fontSize: 12, color: T.textMuted, lineHeight: 1.6, fontFamily: F.body }}
      >
        {count === 1 ? 'This photo appears' : `These ${count} photos appear`} on the public page
        exactly as shown, so tap each to re-check. Faces are blurred automatically, but license
        plates, street signs, and everything else in the background are not. Any invoice or document
        you attach will show its prices and the shop&apos;s name.
      </p>

      {/* Review-only modal */}
      {selectedPhoto !== null && selectedIndex !== null ? (
        <ReviewModal
          vehicleId={vehicleId}
          photoId={selectedPhoto.photoId}
          index={selectedIndex}
          total={count}
          onClose={closeModal}
          onMadePrivate={handleMadePrivate}
        />
      ) : null}
    </div>
  );
}
